import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { CampaignReportSandboxItem } from 'api/campaignReportSandbox'
import Modal from 'components/ModalContainer'
import NoSearchResultsFound from 'components/NoSearchResultsFound'
import SearchInput from 'components/SearchInput'
import SideNav from 'components/SideNav'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap-five'
import { merchantTheme } from 'theme'
import ReportItem from './ReportItem'

const t = buildTranslate('thanx_campaigns.report.v3.reports.report_selector')

type Props = {
  isOpen: boolean
  onClose: () => void
  reports: CampaignReportSandboxItem[]
  selectedReportId: string
  onSelectReport: (reportId: string) => void
  isLoading: boolean
}

const ReportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  reports,
  selectedReportId,
  onSelectReport,
  isLoading,
}) => {
  const [css] = useStyletron()
  const [searchQuery, setSearchQuery] = useState('')

  // Get all unique categories
  const categories = useMemo(() => {
    return Array.from(
      new Set(reports.map(item => item.category)).values()
    ).sort((a, b) => a.localeCompare(b))
  }, [reports])

  // Initialize selected category to the category of the currently selected report
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    const current = reports.find(item => item.id === selectedReportId)
    return current?.category ?? categories[0] ?? ''
  })

  // Update selected category when categories change (on initial load)
  useEffect(() => {
    if (!selectedCategory && categories.length > 0) {
      setSelectedCategory(categories[0])
    }
  }, [categories, selectedCategory])

  // Sync selected category when selectedReportId changes from outside
  useEffect(() => {
    const current = reports.find(item => item.id === selectedReportId)
    if (current?.category) {
      setSelectedCategory(current.category)
    }
  }, [selectedReportId, reports])

  const filteredReports = useMemo(() => {
    let results = [...reports]

    // Apply category filter
    if (selectedCategory && searchQuery.trim() === '') {
      results = results.filter(item => item.category === selectedCategory)
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      results = results.filter(
        item =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    return results
  }, [reports, selectedCategory, searchQuery])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category)
  }

  const handleSelect = (reportId: string) => {
    onSelectReport(reportId)
    setSearchQuery('')
  }

  // Reset search query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('select_report_title')}
      hideCancel
      showBottomProceed={false}
      width="1200px"
      dialogStyle={{ maxWidth: '95vw' }}
    >
      <div data-testid="report-search-input">
        <SearchInput
          className="w-100 mb-xl"
          onChange={handleSearchChange}
          value={searchQuery}
          placeholder={t('search_placeholder')}
        />
      </div>
      <Spinner
        className="d-flex justify-content-center"
        size="3x"
        isLoading={isLoading}
      >
        <Switch
          condition={filteredReports.length > 0}
          fallback={<NoSearchResultsFound />}
        >
          <Row className={css({ height: '500px', margin: 0 })}>
            <Switch condition={searchQuery.trim() === ''}>
              <Col
                md={3}
                className={css({
                  height: '100%',
                  overflowY: 'auto',
                  paddingLeft: 0,
                  paddingRight: '16px',
                })}
              >
                <SideNav
                  items={categories}
                  current={selectedCategory || ''}
                  onClick={handleCategorySelect}
                  noBorderRadius
                />
              </Col>
            </Switch>
            <Col
              className={css({
                height: '100%',
                overflowY: 'auto',
                paddingLeft: 0,
                paddingRight: 0,
              })}
            >
              <Switch condition={searchQuery.trim() === ''}>
                <div
                  className={css({
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: merchantTheme.colors.grey05,
                    color: merchantTheme.colors.grey90,
                    marginTop: 0,
                  })}
                >
                  <Text.SmallCaps3 bold>{selectedCategory}</Text.SmallCaps3>
                </div>
              </Switch>

              {filteredReports.map(report => (
                <ReportItem
                  key={report.id}
                  id={report.id}
                  title={report.title}
                  description={report.description}
                  isSelected={selectedReportId === report.id}
                  onClick={handleSelect}
                />
              ))}
            </Col>
          </Row>
        </Switch>
      </Spinner>
    </Modal>
  )
}

export default ReportModal
